import { Card, CardContent, CardHeader, CardTitle } from '@pesto/ui/components/Card';
import { LoadingSpinner } from '@pesto/ui/components/LoadingSpinner';
import { Typography } from '@pesto/ui/components/Typography';

import type { LoanMissingServicingData } from '../types/LoansMissingServicingData.types';

import { formatBoardedLabel } from '../utils/formatBoardedLabel';
import { formatMemberNames } from '../utils/formatMemberNames';

export interface LoansMissingServicingDataPanelProps {
  loans: LoanMissingServicingData[] | undefined;
  loading: boolean;
  error: boolean;
  className?: string;
}

export const LoansMissingServicingDataPanel = ({
  loans,
  loading,
  error,
  className,
}: LoansMissingServicingDataPanelProps) => {
  const hasLoans = loans !== undefined;
  const count = loans?.length ?? 0;
  const showInitialLoading = loading && !hasLoans;
  const showError = !showInitialLoading && error && !hasLoans;
  const showRows = hasLoans && !showError && count > 0;
  const showEmpty = hasLoans && !showError && count === 0;

  return (
    <Card className={className} data-testid="loans-missing-servicing-data-panel">
      <CardHeader>
        <CardTitle className="text-lg">
          {hasLoans ? `Loans missing servicing data (${count})` : 'Loans missing servicing data'}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {showInitialLoading && (
          <div className="text-muted-foreground flex items-center gap-2">
            <LoadingSpinner size={16} />
            <Typography variant="bodySmall">Loading…</Typography>
          </div>
        )}
        {showError && (
          <Typography variant="bodySmall" className="text-muted-foreground">
            Could not load the gap list.
          </Typography>
        )}
        {showEmpty && (
          <Typography variant="bodySmall" className="text-muted-foreground">
            All loans have servicing data.
          </Typography>
        )}
        {showRows && (
          <ul className="divide-border divide-y">
            {(loans ?? []).map((loan) => (
              <li
                key={loan.id}
                className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 py-2 first:pt-0 last:pb-0"
                data-testid="loans-missing-servicing-data-row"
              >
                <div className="flex flex-wrap items-baseline gap-x-3">
                  <span className="text-foreground font-mono text-sm">{loan.partnerAccountNumber}</span>
                  <span className="text-foreground text-sm">{formatMemberNames(loan.members)}</span>
                </div>
                <span className="text-muted-foreground text-sm">
                  {formatBoardedLabel(loan.status, loan.originationDate)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};
